import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';
import type { ButtonProps } from '../button';

export type ButtonGroupVariant = 'default' | 'outline' | 'secondary';

const groupVariantToButtonVariant: Record<
  ButtonGroupVariant,
  NonNullable<ButtonProps['variant']>
> = {
  default: 'default',
  outline: 'outline',
  secondary: 'secondary',
};

const buttonGroupVariants = cva('inline-flex w-fit', {
  variants: {
    variant: {
      default: 'overflow-hidden rounded-md shadow-2xs',
      secondary: 'overflow-hidden rounded-md shadow-2xs',
      outline: 'rounded-md shadow-2xs',
    },
    orientation: {
      horizontal: 'flex-row',
      vertical: 'flex-col',
    },
  },
  compoundVariants: [
    {
      variant: 'default',
      orientation: 'horizontal',
      class: 'divide-x divide-primary-foreground/20',
    },
    {
      variant: 'default',
      orientation: 'vertical',
      class: 'divide-y divide-primary-foreground/20',
    },
    {
      variant: 'secondary',
      orientation: 'horizontal',
      class: 'divide-x divide-input',
    },
    {
      variant: 'secondary',
      orientation: 'vertical',
      class: 'divide-y divide-input',
    },
  ],
  defaultVariants: {
    variant: 'default',
    orientation: 'horizontal',
  },
});

function segmentClassName(
  index: number,
  total: number,
  orientation: 'horizontal' | 'vertical',
  groupVariant: ButtonGroupVariant
): string {
  const isFirst = index === 0;
  const isLast = index === total - 1;
  const only = total === 1;
  const z = 'relative z-0 focus-visible:z-10';

  if (groupVariant === 'outline') {
    if (orientation === 'horizontal') {
      return cn(
        'shadow-none',
        z,
        only && 'rounded-md',
        !only && isFirst && 'rounded-l-md rounded-r-none',
        !only && isLast && 'rounded-r-md rounded-l-none',
        !only && !isFirst && !isLast && 'rounded-none',
        !only && !isFirst && '-ml-px'
      );
    }
    return cn(
      'shadow-none',
      z,
      only && 'rounded-md',
      !only && isFirst && 'rounded-t-md rounded-b-none',
      !only && isLast && 'rounded-b-md rounded-t-none',
      !only && !isFirst && !isLast && 'rounded-none',
      !only && !isFirst && '-mt-px'
    );
  }

  if (orientation === 'horizontal') {
    return cn(
      'shadow-none',
      z,
      only && 'rounded-md',
      !only && isFirst && 'rounded-l-md rounded-r-none',
      !only && isLast && 'rounded-r-md rounded-l-none',
      !only && !isFirst && !isLast && 'rounded-none'
    );
  }

  return cn(
    'shadow-none',
    z,
    only && 'rounded-md',
    !only && isFirst && 'rounded-t-md rounded-b-none',
    !only && isLast && 'rounded-b-md rounded-t-none',
    !only && !isFirst && !isLast && 'rounded-none'
  );
}

export interface ButtonGroupProps
  extends ComponentPropsWithoutRef<'div'>, VariantProps<typeof buttonGroupVariants> {
  children: ReactNode;
}

const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
  ({ className, variant = 'default', orientation = 'horizontal', children, ...props }, ref) => {
    const items = Children.toArray(children).filter(Boolean);
    const count = items.length;
    const v = variant ?? 'default';
    const o = orientation ?? 'horizontal';
    const mapped = groupVariantToButtonVariant[v];

    return (
      <div
        ref={ref}
        role="group"
        data-slot="button-group"
        className={cn(buttonGroupVariants({ variant: v, orientation: o }), className)}
        {...props}
      >
        {items.map((child, index) => {
          if (!isValidElement(child)) return child;
          const el = child as ReactElement<ButtonProps>;
          const bp = el.props;
          return cloneElement(el, {
            variant: bp.variant ?? mapped,
            className: cn(bp.className, segmentClassName(index, count, o, v)),
          });
        })}
      </div>
    );
  }
);
ButtonGroup.displayName = 'ButtonGroup';

export { ButtonGroup, buttonGroupVariants };
